// ============================================================================
// Solver de PREÇO DE VENDA (inverso da margem) usando os motores oficiais
// de custo do Mercado Livre (meliCost) e da Shopee (shopeeCost).
//
// O custo de venda (CAV) num preço P tem a forma:  F(P) + P·v
//   v       = (comissão% + ads% + imposto%)            (parte proporcional)
//   F(P)    = holding + frete(P)/taxaFixa(P) + Full     (parte em R$, degrau de P)
//
// Margem percentual:  P = (custoAnuncio + F(P)) / (1 − v − margemAlvo)
// Margem em R$:       P = (custoAnuncio + F(P) + margemReais) / (1 − v)
//
// Como F(P) é degrau no preço (faixas de frete do ML / R$4 vs 50% da Shopee),
// iteramos os segmentos de preço e escolhemos o ponto fixo consistente.
// ============================================================================

import { MLCostEngine } from './meliCost.js';
import { ShopeeEngine } from './shopeeCost.js';

// Limites de preço onde o frete do ML muda (alinhados às colunas oficiais).
const MELI_BREAKS = [0.01, 19, 49, 79, 100, 120, 150, 200, Infinity];

function denominador(p, v) {
    return p.modoMargem === 'reais' ? (1 - v) : (1 - v - p.margemAlvo);
}
function extraReais(p) {
    return p.modoMargem === 'reais' ? (p.margemAlvoReais || 0) : 0;
}

function montar(canal, P, p, partes) {
    const { comm, logistica, fullArmazenagem = 0, fullEstoque = 0, tamanho = null } = partes;
    const comissaoValor = P * comm / 100;
    const adsValor = P * (p.adsPct || 0) / 100;
    const impostoValor = P * (p.impostoPct || 0) / 100;
    const holding = p.holding || 0;
    const cav = holding + logistica + comissaoValor + adsValor + impostoValor;
    const recebe = P - cav;
    const margem = recebe - p.custoAnuncio;
    const margemPercent = P > 0 ? margem / P : 0;
    // Indicador de saúde da margem (usado no Raio-X)
    let saudeIndicador, saudeCor, saudeTexto;
    if (margem < 0) { saudeIndicador = '💀'; saudeCor = '#991b1b'; saudeTexto = 'Prejuízo'; }
    else if (margemPercent < 0.10) { saudeIndicador = '❌'; saudeCor = '#ef4444'; saudeTexto = 'Crítica'; }
    else if (margemPercent < 0.20) { saudeIndicador = '⚠️'; saudeCor = '#f59e0b'; saudeTexto = 'Apertada'; }
    else { saudeIndicador = '✅'; saudeCor = '#10b981'; saudeTexto = 'Saudável'; }
    return {
        canal,
        precoVenda: P,
        saudeIndicador, saudeCor, saudeTexto,
        labelVar: canal === 'meli' ? 'Comissão' : 'Comissão',
        // compatível com o shape antigo do app:
        txHolding: holding,
        txFixa: logistica,              // ML: frete + Full · Shopee: taxa fixa por item
        labelFixa: canal === 'meli' ? (fullArmazenagem + fullEstoque > 0 ? 'Frete + Full' : 'Frete') : 'Taxa fixa',
        txVariavel: comm / 100,
        txVariavelValor: comissaoValor,
        ads: (p.adsPct || 0) / 100,
        adsValor,
        imposto: (p.impostoPct || 0) / 100,
        impostoValor,
        cav,
        recebe,
        custoAnuncio: p.custoAnuncio,
        margem,
        margemPercent: P > 0 ? margem / P : 0,
        // detalhes extras (ML):
        comissaoPct: comm,
        frete: canal === 'meli' ? logistica - fullArmazenagem - fullEstoque : undefined,
        fullArmazenagem,
        fullEstoque,
        tamanhoFull: tamanho
    };
}

function resolverMeli(p) {
    const comm = p.tipoAnuncio === 'premium' ? p.comissaoPremium
        : p.tipoAnuncio === 'free' ? 0 : p.comissaoClassico;
    const v = (comm + (p.adsPct || 0) + (p.impostoPct || 0)) / 100;
    const den = denominador(p, v);
    if (den <= 0) return { erro: 'Margem impossível' };
    const full = MLCostEngine.custoFullFixo(p);
    const fixoFull = full.armazenagem + full.estoqueAntigo;
    const F0 = (p.holding || 0) + fixoFull;
    const extra = extraReais(p);

    let best = null;
    for (let i = 0; i < MELI_BREAKS.length - 1; i++) {
        const lo = MELI_BREAKS[i], hi = MELI_BREAKS[i + 1];
        const probe = isFinite(hi) ? (lo + hi) / 2 : lo + 1;
        const frete = MLCostEngine.frete({ ...p, preco: probe });
        const P = (p.custoAnuncio + F0 + frete + extra) / den;
        const consistente = P >= lo && (P < hi || !isFinite(hi));
        if (consistente) { best = { P, frete }; break; }
        // fallback: guarda o candidato cujo P está mais perto do segmento
        const dist = P < lo ? lo - P : (isFinite(hi) ? P - hi : 0);
        if (!best || dist < best.dist) best = { P, frete, dist };
    }
    const freteFinal = MLCostEngine.frete({ ...p, preco: best.P });
    return montar('meli', best.P, p, {
        comm, logistica: freteFinal + fixoFull,
        fullArmazenagem: full.armazenagem, fullEstoque: full.estoqueAntigo, tamanho: full.tamanho
    });
}

// Shopee é DEGRAU: comissão (20%/14%) E taxa fixa (50%/4/16/20/26) mudam com o
// preço. Iteramos os segmentos e escolhemos o ponto fixo consistente.
const SHOPEE_SEGS = [
    { lo: 0.01, hi: 8, comm: 0.20, fixaLinear: true }, // taxa = 50% do preço
    { lo: 8, hi: 80, comm: 0.20, fixa: 4 },
    { lo: 80, hi: 100, comm: 0.14, fixa: 16 },
    { lo: 100, hi: 200, comm: 0.14, fixa: 20 },
    { lo: 200, hi: Infinity, comm: 0.14, fixa: 26 },
];

function resolverShopee(p) {
    const ads = (p.adsPct || 0) / 100;
    const imp = (p.impostoPct || 0) / 100;
    const holding = p.holding || 0;
    const extra = extraReais(p);
    const margem = p.modoMargem === 'reais' ? 0 : p.margemAlvo;

    let best = null, bestDist = Infinity;
    for (const s of SHOPEE_SEGS) {
        let den, P;
        if (s.fixaLinear) {
            // taxa fixa = 50% do preço entra como termo proporcional
            den = 1 - s.comm - ads - imp - margem - 0.5;
            if (den <= 0) continue;
            P = (p.custoAnuncio + holding + extra) / den;
        } else {
            den = 1 - s.comm - ads - imp - margem;
            if (den <= 0) continue;
            P = (p.custoAnuncio + holding + s.fixa + extra) / den;
        }
        if (P >= s.lo && (P < s.hi || !isFinite(s.hi))) { best = P; bestDist = 0; break; }
        const dist = P < s.lo ? s.lo - P : (isFinite(s.hi) ? P - s.hi : 0);
        if (dist < bestDist) { best = P; bestDist = dist; }
    }
    if (best == null) return { erro: 'Margem impossível' };
    return montar('shopee', best, p, { comm: ShopeeEngine.comissaoPct(best), logistica: ShopeeEngine.taxaFixaItem(best) });
}

/**
 * Calcula o preço de venda para a margem-alvo, 100% conforme as taxas da plataforma.
 * @param {object} p parâmetros (ver topo do arquivo + por canal)
 */
export function precoDeVenda(p) {
    return (p.canal === 'shopee') ? resolverShopee(p) : resolverMeli(p);
}

/**
 * Resultado FORWARD num preço fixo (para a tabela de simulação ±%).
 * Usa os mesmos motores de custo, com o frete recalculado na faixa do preço.
 */
export function resultadoNoPreco(p, preco) {
    if (p.canal === 'shopee') {
        return montar('shopee', preco, p, { comm: ShopeeEngine.comissaoPct(preco), logistica: ShopeeEngine.taxaFixaItem(preco) });
    }
    const comm = p.tipoAnuncio === 'premium' ? p.comissaoPremium
        : p.tipoAnuncio === 'free' ? 0 : p.comissaoClassico;
    const full = MLCostEngine.custoFullFixo(p);
    const frete = MLCostEngine.frete({ ...p, preco });
    return montar('meli', preco, p, {
        comm, logistica: frete + full.armazenagem + full.estoqueAntigo,
        fullArmazenagem: full.armazenagem, fullEstoque: full.estoqueAntigo, tamanho: full.tamanho
    });
}
